import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormArray, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { DevelopmentService } from '@app/_components/development/development.service';
import { VrboService } from '@app/_services/vrbo.service';
import { forkJoin } from 'rxjs';
import * as moment from 'moment';

interface TierRow {
  tierLabel: string;
  memberTier: string;
  unit: string;
  value: number | null;
}

interface BulkProperty {
  propertyId: string;
  propertyCode: string;
  shortTermRentalId: string;
  selected: boolean;
  status: string; // '' | 'running' | 'success' | 'failed'
  error: string;
}

interface DayOfWeekRow {
  dayLabel: string;
  day: string;
  unit: string;
  value: number | null;
}

@Component({
    selector: 'app-vrbo-promotions',
    templateUrl: './vrbo-promotions.component.html',
    styleUrls: ['./vrbo-promotions.component.css'],
    standalone: false
})
export class VRBOPromotionsComponent implements OnInit, OnChanges {
  @Input() editPromotion: any = null;

  form!: UntypedFormGroup;
  formLoading = false;
  isEditMode = false;
  editPromotionId: string | null = null;
  today = new Date();
  readonly EVERGREEN_DATE = '2079-06-06';
  readonly EVERGREEN_DATE_OBJ = new Date('2079-06-06T12:00:00'); // noon avoids timezone off-by-one
  maxTravelToDate: Date | null = null; // null = no upper limit; set to today+2yr for EARLY_BOOKING
  isEvergreen = false;

  // Data sources
  allProperties: any[] = [];
  allDevelopments: any[] = [];
  filteredDevelopments: any[] = [];
  locations: any[] = [];

  // Selection mode
  selectionModes = [
    { label: 'By Unit', value: 'BY_UNIT' },
    { label: 'By Development', value: 'BY_DEVELOPMENT' },
    { label: 'By Location', value: 'BY_LOCATION' }
  ];

  // Promotion types
  promotionTypes = [
    { label: 'Early Booking', value: 'EARLY_BOOKING' },
    { label: 'Last Minute', value: 'LAST_MINUTE' },
    { label: 'New Listing Discount', value: 'NEW_LISTING' },
    { label: 'Member Only', value: 'MEMBER_ONLY' },
    { label: 'Mobile Only', value: 'MOBILE_ONLY' },
    { label: 'Wiki Coupon', value: 'WIKI_COUPON' }
  ];

  statusOptions = [
    { label: 'ACTIVE', value: 'ACTIVE' },
    { label: 'INACTIVE', value: 'INACTIVE' }
  ];

  discountUnits = [
    { label: 'PERCENT', value: 'PERCENT' },
    { label: 'AMOUNT', value: 'AMOUNT' }
  ];

  discountTypes = [
    { label: 'Flat', value: 'FLAT' },
    { label: 'Multi-Night', value: 'MULTI_NIGHT' }
  ];

  discountModes = [
    { label: 'Single', value: 'SINGLE' },
    { label: 'Day-of-Week', value: 'DAY_OF_WEEK' }
  ];

  selectedDiscountMode = 'SINGLE';

  dayOfWeekRows: DayOfWeekRow[] = [
    { dayLabel: 'Monday', day: 'monday', unit: 'PERCENT', value: null },
    { dayLabel: 'Tuesday', day: 'tuesday', unit: 'PERCENT', value: null },
    { dayLabel: 'Wednesday', day: 'wednesday', unit: 'PERCENT', value: null },
    { dayLabel: 'Thursday', day: 'thursday', unit: 'PERCENT', value: null },
    { dayLabel: 'Friday', day: 'friday', unit: 'PERCENT', value: null },
    { dayLabel: 'Saturday', day: 'saturday', unit: 'PERCENT', value: null },
    { dayLabel: 'Sunday', day: 'sunday', unit: 'PERCENT', value: null }
  ];

  promotionTooltips: { [key: string]: string } = {
    'EARLY_BOOKING': 'EARLY BOOKING PROMOTION — Rewards travelers who plan ahead by booking well in advance of their stay. ' +
      '\n\nWhen to use: Best for high-demand seasons (summer, holidays) where you want to lock in bookings months ahead and reduce last-minute vacancy risk. ' +
      '\n\nRequired fields: Property ID, Status (ACTIVE/INACTIVE), Discount (either Single Discount OR Day-of-Week Discount), ' +
      'Advance Booking Window (Min & Max Days), Travel Date Range (From & To). ' +
      '\n\nValidation rules: Advance booking days must be between 60 and 500 (min >= 60, max <= 500, min <= max). ' +
      'Percent discount must be 0-75%. Amount discount must be > 0. Stay duration min >= 1, max <= 180 if provided. ' +
      '\n\nOptional fields: Stay Duration (Min/Max Nights), Blackout Dates. Day-of-Week supports different discount values for each weekday (Mon-Sun). ' +
      '\n\nVRBO GraphQL API: Uses createPromotion mutation with promotionType: EARLY_BOOKING. ' +
      'Maps to incentive.singleDiscount and restrictions.advanceBookingDays, travelDateRange, lengthOfStay, blackoutDateRanges. ' +
      '\n\nBackend endpoint: POST /vrbo/v3/promotions/early-booking. ' +
      '\n\nExample: 15% off for stays Jun 1 - Aug 31, 2026, when booked at least 90 days before check-in (min 90, max 500). ' +
      'Add blackout dates for Jul 4 week to protect holiday pricing.',

    'LAST_MINUTE': 'LAST MINUTE PROMOTION — Targets travelers who book close to their check-in date, perfect for filling calendar gaps. ' +
      '\n\nWhen to use: Ideal for slow periods, shoulder seasons, or when you notice unbooked nights within the next 1-4 weeks. ' +
      'Helps convert last-minute browsers into bookings with an attractive discount. ' +
      '\n\nRequired fields: Property ID, Status (ACTIVE/INACTIVE), Discount (either Single Discount OR Day-of-Week Discount), ' +
      'Advance Booking Window (Min & Max Days), Travel Date Range (From & To). ' +
      '\n\nValidation rules: Advance booking days must be between 1 and 30 (min >= 1, max <= 30, min <= max). ' +
      'Percent discount must be 0-75%. Amount discount must be > 0. Stay duration min >= 1, max <= 180 if provided. ' +
      '\n\nOptional fields: Stay Duration (Min/Max Nights), Blackout Dates. Day-of-Week supports different discount values for each weekday (Mon-Sun). ' +
      '\n\nVRBO GraphQL API: Uses createPromotion mutation with promotionType: LAST_MINUTE. ' +
      'Maps to incentive.singleDiscount and restrictions.advanceBookingDays, travelDateRange, lengthOfStay, blackoutDateRanges. ' +
      '\n\nBackend endpoint: POST /vrbo/v3/promotions/last-minute. ' +
      '\n\nExample: 20% off for stays in the next 2 weeks, bookable up to 14 days before check-in (min 1, max 14). ' +
      'Great for filling mid-week gaps or shoulder season openings.',

    'NEW_LISTING': 'NEW LISTING DISCOUNT — Available ONLY for properties within their first 90-day onboarding window on VRBO. ' +
      'Designed to boost visibility and attract initial bookings for newly listed properties. ' +
      '\n\nWhen to use: Immediately after listing a new property on VRBO. The 90-day clock starts from the listing date. ' +
      'Use this to compete with established listings by offering competitive pricing during the critical launch period. ' +
      '\n\nRequired fields: Property ID, Status (ACTIVE/INACTIVE), Travel Date Range (From & To). ' +
      '\n\nOptional fields: Discount Unit & Value OR Day-of-Week Discount (if both are omitted, defaults to 20% PERCENT), ' +
      'Stay Duration (Min/Max Nights), Blackout Dates. Advance booking window is NOT applicable. ' +
      '\n\nValidation rules: Property must pass VRBO eligibility check (promotionEligibility query) — verified automatically before creation. ' +
      'If the property has exceeded 90 days since listing, creation will be rejected with ineligibility reasons. ' +
      'Percent discount: 1-75%. Amount discount: > 0. Day-of-Week requires all 7 days with valid values. Stay duration min >= 1, max <= 180 if provided. ' +
      '\n\nVRBO GraphQL API: Uses createPromotion mutation with promotionType: NEW_LISTING_DISCOUNT. ' +
      'Calls promotionEligibility query first to check eligibilityStatus, remainingDays, validBookingDateRange. ' +
      '\n\nBackend endpoint: POST /vrbo/v3/promotions/new-listing-discount. ' +
      '\n\nExample: Default 20% off for a newly listed Maui condo with stays Sep 1 - Nov 30, 2025. ' +
      'Or override to 25% off to be more aggressive during the launch window.',

    'MEMBER_ONLY': 'MEMBER ONLY PROMOTION — Exclusive to VRBO loyalty program members, with tiered discounts for Blue, Silver, and Gold members. ' +
      '\n\nWhen to use: To attract loyal VRBO travelers who book frequently. Member-only deals appear with a special badge on VRBO, ' +
      'increasing click-through rates from high-value guests. Works well for repeat-visitor destinations. ' +
      '\n\nRequired fields: Property ID, Status (ACTIVE/INACTIVE), Travel Date Range (From & To), ' +
      'Discount Type (Flat or Multi-Night), Tier Configuration (all 3 tiers: BLUE, SILVER, GOLD — each with Unit and Value). ' +
      '\n\nFlat Discount (SINGLE_DISCOUNT): Each tier gets a straightforward percent or amount discount. ' +
      'Example: Blue 10%, Silver 15%, Gold 20%. All three tiers must be configured. ' +
      '\n\nMulti-Night Discount (MULTI_NIGHT_DISCOUNT): The discount applies to a specific night of the stay. ' +
      'Requires Applicable Night (>= 2, e.g. 3 = the 3rd night is discounted). ' +
      'Optional Recurring toggle: ON = discount repeats every Nth night (e.g. nights 3, 6, 9); OFF = only first occurrence. ' +
      'IMPORTANT: Min stay duration MUST be >= Applicable Night value, otherwise VRBO rejects the request. ' +
      '\n\nValidation rules: All 3 tiers required with memberTier, discount.unit, and discount.value. ' +
      'Percent: 1-75% per tier. Amount: > 0 per tier. Stay duration min >= 1, max <= 180. ' +
      '\n\nOptional fields: Advance Booking Window (Min/Max Days), Stay Duration (Min/Max Nights), Blackout Dates. ' +
      '\n\nVRBO GraphQL API: Uses createPromotion mutation with promotionType: MEMBER_ONLY. ' +
      'Maps to incentive.tieredDiscount.tiers[] with discountType: SINGLE_DISCOUNT or MULTI_NIGHT_DISCOUNT. ' +
      '\n\nBackend endpoint: POST /vrbo/v3/promotions/member-only. ' +
      '\n\nExample (Flat): Blue 10%, Silver 15%, Gold 20% for stays Dec 2025 - Mar 2026. ' +
      'Example (Multi-Night): Every 3rd night free for Gold members, every 4th night free for Silver/Blue — with 5-night minimum stay.',

    'MOBILE_ONLY': 'MOBILE ONLY PROMOTION — Applies exclusively to bookings made through the VRBO mobile app or mobile web browser. ' +
      '\n\nWhen to use: To capture the growing segment of mobile-first travelers. VRBO reports that a significant portion of bookings come from mobile. ' +
      'The promotion appears with a "Mobile Deal" badge visible only to mobile users, creating urgency and exclusivity. ' +
      '\n\nRequired fields: Property ID, Status (ACTIVE/INACTIVE), Discount (either Single Discount OR Day-of-Week Discount), ' +
      'Travel Date Range (From & To). ' +
      '\n\nValidation rules: Percent discount must be 0-75%. Amount discount must be > 0. Day-of-Week requires all 7 days with valid values. ' +
      'Stay duration min >= 1, max <= 180 if provided. ' +
      '\n\nOptional fields: Advance Booking Window (Min/Max Days — leave blank for no restriction), ' +
      'Stay Duration (Min/Max Nights), Blackout Dates. ' +
      '\n\nVRBO GraphQL API: Uses createPromotion mutation with promotionType: MOBILE_ONLY. ' +
      'Maps to incentive.singleDiscount and restrictions.travelDateRange, lengthOfStay, blackoutDateRanges. ' +
      '\n\nBackend endpoint: POST /vrbo/v3/promotions/mobile-only. ' +
      '\n\nExample: 10% off for all mobile bookings with stays between Dec 2025 and Mar 2026. ' +
      'No advance booking restriction — available to any mobile user who books within the travel window. ' +
      'Combine with a Last Minute promotion on desktop for full coverage.',

    'WIKI_COUPON': 'WIKI COUPON — A locally managed discount coupon code (not sent to VRBO). ' +
      'Guests enter the code during booking on the KBM website to receive a discount on nightly rates. ' +
      '\n\nWhen to use: For promotional campaigns, influencer codes, seasonal discounts, or special event coupons. ' +
      '\n\nRequired fields: Coupon Code, Discount Value, Booking Window (From & To), Travel Dates (From & To). ' +
      '\n\nOptional: Auto-Applied (coupon applies automatically without guest entering code), Apply to specific properties only. ' +
      '\n\nBackend endpoint: POST /vrbo/v3/promotions/wiki-coupon.'
  };

  memberTiers: TierRow[] = [
    { tierLabel: 'Blue', memberTier: 'BLUE', unit: 'PERCENT', value: 10 },
    { tierLabel: 'Silver', memberTier: 'SILVER', unit: 'PERCENT', value: 15 },
    { tierLabel: 'Gold', memberTier: 'GOLD', unit: 'PERCENT', value: 20 }
  ];

  // Resolved properties preview
  resolvedProperties: any[] = [];
  showPreview = false;
  resolvedCount = 0;

  // Override discount for New Listing
  overrideDiscount = false;

  // Discount type for Member Only
  selectedDiscountType = 'FLAT';

  // Bulk creation
  showConfirmDialog = false;
  showProgressDialog = false;
  bulkProperties: BulkProperty[] = [];
  bulkProgress = 0;
  bulkTotal = 0;
  bulkSuccessCount = 0;
  bulkFailCount = 0;
  bulkCurrentProperty = '';
  bulkComplete = false;

  constructor(
    private fb: UntypedFormBuilder,
    private messageService: MessageService,
    private developmentService: DevelopmentService,
    private vrboService: VrboService
  ) {}

  ngOnInit(): void {
    this.buildForm();
    this.loadData();
    if (this.editPromotion) {
      this.populateFormForEdit(this.editPromotion);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['editPromotion'] && !changes['editPromotion'].firstChange && this.editPromotion) {
      this.populateFormForEdit(this.editPromotion);
    }
  }

  populateFormForEdit(p: any): void {
    this.isEditMode = true;
    this.editPromotionId = p.id || null;

    // Set promotion type first
    this.form.patchValue({ promotionType: p.promotionType });

    if (p.promotionType === 'WIKI_COUPON') {
      const travelFrom = p.restrictions?.travelDateRange?.from
        ? new Date(p.restrictions.travelDateRange.from) : null;
      const travelTo = p.restrictions?.travelDateRange?.to
        ? new Date(p.restrictions.travelDateRange.to) : null;
      const bookingFrom = p.bookingWindowFrom ? new Date(p.bookingWindowFrom) : null;
      const bookingTo = p.bookingWindowTo ? new Date(p.bookingWindowTo) : null;

      this.form.patchValue({
        couponCode: p.couponCode || '',
        couponName: p.couponName || p.couponCode || '',
        status: p.status || 'ACTIVE',
        discountUnit: p.discountUnit || 'PERCENT',
        discountValue: p.discountValue || null,
        travelDateFrom: travelFrom,
        travelDateTo: travelTo,
        bookingWindowFrom: bookingFrom,
        bookingWindowTo: bookingTo,
        isAutoApplied: p.isAutoApplied || false,
        applyToAllProperties: true
      });
    } else {
      // For VRBO promotion types
      this.form.patchValue({
        status: p.status || 'ACTIVE'
      });

      // Populate discount from incentive
      const single = p.incentive?.discountIncentive?.discount?.single?.singleDiscount;
      if (single?.value != null) {
        this.form.patchValue({
          discountUnit: single.unit || 'PERCENT',
          discountValue: single.value
        });
      }

      // Populate travel dates from restrictions
      const travelRange = p.restrictions?.travelDateRange;
      if (travelRange) {
        if (travelRange.from) this.form.patchValue({ travelDateFrom: new Date(travelRange.from) });
        if (travelRange.to) {
          const toDate = new Date(travelRange.to);
          this.form.patchValue({ travelDateTo: toDate });
          // Restore evergreen state and maxDate limit for Early Booking edits
          if (p.promotionType === 'EARLY_BOOKING') {
            const twoYearsFromNow = new Date();
            twoYearsFromNow.setFullYear(twoYearsFromNow.getFullYear() + 2);
            this.maxTravelToDate = twoYearsFromNow;
            if (this.isEvergreenDate(toDate)) {
              this.isEvergreen = true;
            }
          }
        }
      }

      // Populate advance booking window
      const advBooking = p.restrictions?.advanceBookingDaysRange;
      if (advBooking) {
        this.form.patchValue({
          advanceBookingMin: advBooking.min,
          advanceBookingMax: advBooking.max
        });
      }

      // Populate stay duration
      const stayDuration = p.restrictions?.stayDurationRange;
      if (stayDuration) {
        this.form.patchValue({
          stayDurationMin: stayDuration.min,
          stayDurationMax: stayDuration.max
        });
      }
    }
  }

  buildForm(): void {
    this.form = this.fb.group({
      selectionMode: ['BY_UNIT'],
      selectedUnits: [[]],
      selectedLocation: [null],
      selectedDevelopments: [[]],
      selectedLocations: [[]],
      promotionType: [null, Validators.required],
      status: ['ACTIVE', Validators.required],
      discountUnit: ['PERCENT'],
      discountValue: [null],
      travelDateFrom: [null, Validators.required],
      travelDateTo: [null, Validators.required],
      advanceBookingMin: [null],
      advanceBookingMax: [null],
      stayDurationMin: [1],
      stayDurationMax: [180],
      applicableNight: [null],
      recurring: [false],
      couponCode: [null],
      couponName: [null],
      bookingWindowFrom: [null],
      bookingWindowTo: [null],
      isAutoApplied: [false],
      applyToAllProperties: [true]
    });
  }

  loadData(): void {
    this.formLoading = true;
    forkJoin([
      this.vrboService.findAllPropertiesLite(),
      this.developmentService.findAll(),
      this.vrboService.getLocations()
    ]).subscribe(
      ([propRes, devRes, locRes]: any[]) => {
        this.allProperties = (propRes?.data?.properties || []).map((p: any) => ({
          ...p,
          displayLabel: `${p.propertyCode} — STR: ${p.shortTermRentalId}${p.vrboId && p.vrboId !== p.shortTermRentalId ? ' / VRBO: ' + p.vrboId : ''}`
        }));

        const devs = devRes?.data?.developments || devRes?.data || [];
        this.allDevelopments = Array.isArray(devs) ? devs : [];
        this.filteredDevelopments = [...this.allDevelopments];

        // Use locations from backend
        const locs = locRes?.data?.locations || [];
        this.locations = locs.map((l: any) => ({
          locationId: l.locationId,
          label: l.name
        }));

        this.formLoading = false;
      },
      (err: any) => {
        this.formLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load data: ' + (err?.message || err) });
      }
    );
  }

  // Selection mode changes
  onSelectionModeChange(): void {
    this.resolvedProperties = [];
    this.resolvedCount = 0;
    this.showPreview = false;
    this.form.patchValue({
      selectedUnits: [],
      selectedLocation: null,
      selectedDevelopments: [],
      selectedLocations: []
    });
  }

  onLocationFilterChange(): void {
    const locId = this.form.value.selectedLocation;
    if (locId) {
      this.filteredDevelopments = this.allDevelopments.filter((d: any) => d.locationId === locId);
    } else {
      this.filteredDevelopments = [...this.allDevelopments];
    }
    this.form.patchValue({ selectedDevelopments: [] });
    this.resolveProperties();
  }

  onUnitSelectionChange(): void {
    this.resolveProperties();
  }

  onDevelopmentSelectionChange(): void {
    this.resolveProperties();
  }

  onLocationSelectionChange(): void {
    this.resolveProperties();
  }

  resolveProperties(): void {
    const mode = this.form.value.selectionMode;

    if (mode === 'BY_UNIT') {
      const selectedIds = this.form.value.selectedUnits || [];
      const resolved = this.allProperties.filter((p: any) => selectedIds.includes(p.shortTermRentalId));
      this.resolvedProperties = resolved;
      this.resolvedCount = resolved.length;
      this.showPreview = resolved.length > 0;
    } else if (mode === 'BY_DEVELOPMENT') {
      const devIds = this.form.value.selectedDevelopments || [];
      if (!devIds.length) {
        this.resolvedProperties = [];
        this.resolvedCount = 0;
        this.showPreview = false;
        return;
      }
      this.vrboService.getPropertiesByDevelopment(devIds).subscribe(
        (res: any) => {
          const props = (res?.data?.properties || []).map((p: any) => ({
            ...p,
            displayLabel: `${p.propertyCode} — STR: ${p.shortTermRentalId}${p.vrboId && p.vrboId !== p.shortTermRentalId ? ' / VRBO: ' + p.vrboId : ''}`
          }));
          this.resolvedProperties = props;
          this.resolvedCount = props.length;
          this.showPreview = props.length > 0;
        },
        (err: any) => {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to resolve properties by development: ' + (err?.message || err) });
        }
      );
    } else if (mode === 'BY_LOCATION') {
      const locIds = this.form.value.selectedLocations || [];
      if (!locIds.length) {
        this.resolvedProperties = [];
        this.resolvedCount = 0;
        this.showPreview = false;
        return;
      }
      this.vrboService.getPropertiesByLocation(locIds).subscribe(
        (res: any) => {
          const props = (res?.data?.properties || []).map((p: any) => ({
            ...p,
            displayLabel: `${p.propertyCode} — STR: ${p.shortTermRentalId}${p.vrboId && p.vrboId !== p.shortTermRentalId ? ' / VRBO: ' + p.vrboId : ''}`
          }));
          this.resolvedProperties = props;
          this.resolvedCount = props.length;
          this.showPreview = props.length > 0;
        },
        (err: any) => {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to resolve properties by location: ' + (err?.message || err) });
        }
      );
    }
  }

  removeResolvedProperty(index: number): void {
    this.resolvedProperties.splice(index, 1);
    this.resolvedCount = this.resolvedProperties.length;
  }

  // Promotion type change
  onPromotionTypeChange(): void {
    const type = this.form.value.promotionType;
    if (type === 'EARLY_BOOKING') {
      this.form.patchValue({ advanceBookingMin: 60, advanceBookingMax: 500 });
      // Enforce 2-year max on travel end date for Early Booking
      const twoYearsFromNow = new Date();
      twoYearsFromNow.setFullYear(twoYearsFromNow.getFullYear() + 2);
      this.maxTravelToDate = twoYearsFromNow;
      // Reset evergreen when switching type
      this.isEvergreen = false;
      // If current travelDateTo exceeds the limit and isn't the evergreen date, clear it
      const currentTo: Date | null = this.form.value.travelDateTo;
      if (currentTo && !this.isEvergreenDate(currentTo) && currentTo > twoYearsFromNow) {
        this.form.patchValue({ travelDateTo: null });
      }
    } else {
      this.maxTravelToDate = null;
      this.isEvergreen = false;
    }
    if (type === 'LAST_MINUTE') {
      this.form.patchValue({ advanceBookingMin: 1, advanceBookingMax: 30 });
    } else if (type !== 'EARLY_BOOKING') {
      this.form.patchValue({ advanceBookingMin: null, advanceBookingMax: null });
    }
    this.overrideDiscount = false;
    this.selectedDiscountType = 'FLAT';
    this.selectedDiscountMode = 'SINGLE';
    this.dayOfWeekRows = [
      { dayLabel: 'Monday', day: 'monday', unit: 'PERCENT', value: null },
      { dayLabel: 'Tuesday', day: 'tuesday', unit: 'PERCENT', value: null },
      { dayLabel: 'Wednesday', day: 'wednesday', unit: 'PERCENT', value: null },
      { dayLabel: 'Thursday', day: 'thursday', unit: 'PERCENT', value: null },
      { dayLabel: 'Friday', day: 'friday', unit: 'PERCENT', value: null },
      { dayLabel: 'Saturday', day: 'saturday', unit: 'PERCENT', value: null },
      { dayLabel: 'Sunday', day: 'sunday', unit: 'PERCENT', value: null }
    ];
  }

  onDiscountModeChange(): void {
    if (this.selectedDiscountMode === 'DAY_OF_WEEK') {
      // New Listing default-override only applies to single discount mode.
      this.overrideDiscount = false;
    }
  }

  /** Returns true if the given Date represents the special evergreen date 2079-06-06. */
  isEvergreenDate(d: Date | null): boolean {
    if (!d) return false;
    return d.getFullYear() === 2079 && d.getMonth() === 5 && d.getDate() === 6;
  }

  /** Toggles evergreen mode for Early Booking travel end date. */
  toggleEvergreen(): void {
    this.isEvergreen = !this.isEvergreen;
    if (this.isEvergreen) {
      this.form.patchValue({ travelDateTo: this.EVERGREEN_DATE_OBJ });
    } else {
      this.form.patchValue({ travelDateTo: null });
    }
  }

  // Blackout dates
  blackoutDates: Array<{ from: Date | null; to: Date | null }> = [];

  addBlackoutDate(): void {
    this.blackoutDates.push({ from: null, to: null });
  }

  removeBlackoutDate(index: number): void {
    this.blackoutDates.splice(index, 1);
  }

  // Validation
  get isAdvanceBookingRequired(): boolean {
    const type = this.form.value.promotionType;
    return type === 'EARLY_BOOKING' || type === 'LAST_MINUTE';
  }

  get isDiscountRequired(): boolean {
    const type = this.form.value.promotionType;
    return type === 'EARLY_BOOKING' || type === 'LAST_MINUTE' || type === 'MOBILE_ONLY';
  }

  get isMemberOnly(): boolean {
    return this.form.value.promotionType === 'MEMBER_ONLY';
  }

  get isNewListing(): boolean {
    return this.form.value.promotionType === 'NEW_LISTING';
  }

  get isDayOfWeekEligible(): boolean {
    const type = this.form.value.promotionType;
    return type === 'EARLY_BOOKING' || type === 'LAST_MINUTE' || type === 'NEW_LISTING' || type === 'MOBILE_ONLY';
  }

  validate(): string[] {
    const errors: string[] = [];
    const v = this.form.value;

    if (!v.promotionType) {
      errors.push('Promotion type is required');
    }

    if (v.promotionType === 'WIKI_COUPON') {
      if (!v.couponCode || !v.couponCode.trim()) errors.push('Coupon code is required');
      if (v.discountValue == null || v.discountValue <= 0) errors.push('Discount value is required and must be greater than 0');
      if (!v.bookingWindowFrom || !v.bookingWindowTo) errors.push('Booking window (from/to) is required');
      if (!v.applyToAllProperties && this.resolvedCount === 0) errors.push('Select at least one property or enable Apply to All Properties');
      return errors;
    }

    if (this.resolvedCount === 0) {
      errors.push('At least one property must be selected');
    }
    if (!v.travelDateFrom || !v.travelDateTo) {
      errors.push('Travel date range is required');
    } else if (moment(v.travelDateFrom).isAfter(moment(v.travelDateTo))) {
      errors.push('Travel From must be before Travel To');
    } else if (v.promotionType === 'EARLY_BOOKING' && !this.isEvergreenDate(v.travelDateTo)) {
      const twoYearsFromNow = moment().add(2, 'years');
      if (moment(v.travelDateTo).isAfter(twoYearsFromNow)) {
        errors.push('Early Booking travel end date cannot be more than 2 years from today. Use the Evergreen option for open-ended promotions.');
      }
    }

    if (this.isDayOfWeekEligible && this.selectedDiscountMode === 'DAY_OF_WEEK') {
      const invalidDays = this.dayOfWeekRows.filter(d => d.value == null || d.value < 0);
      if (invalidDays.length > 0) {
        errors.push('All 7 days must have a discount value greater than 0');
      }
      this.dayOfWeekRows.forEach(d => {
        if (d.unit === 'PERCENT' && d.value != null && (d.value < 0 || d.value > 75)) {
          errors.push(d.dayLabel + ' percent discount must be between 0 and 75');
        }
      });
    } else if (this.isDiscountRequired) {
      if (!v.discountUnit) errors.push('Discount unit is required');
      if (v.discountValue == null || v.discountValue <= 0) errors.push('Discount value is required and must be greater than 0');
      if (v.discountUnit === 'PERCENT' && (v.discountValue < 0 || v.discountValue > 75)) {
        errors.push('Percent discount must be between 0 and 75');
      }
    } else if (this.isNewListing && this.overrideDiscount) {
      if (v.discountUnit === 'PERCENT' && v.discountValue != null && (v.discountValue < 0 || v.discountValue > 75)) {
        errors.push('Percent discount must be between 0 and 75');
      }
    }

    if (this.isAdvanceBookingRequired) {
      if (v.advanceBookingMin == null || v.advanceBookingMax == null) {
        errors.push('Advance booking days range is required');
      }
      if (v.promotionType === 'EARLY_BOOKING') {
        if (v.advanceBookingMin < 60 || v.advanceBookingMax > 500) {
          errors.push('Early Booking advance days must be between 60 and 500');
        }
      }
      if (v.promotionType === 'LAST_MINUTE') {
        if (v.advanceBookingMin < 1 || v.advanceBookingMax > 30) {
          errors.push('Last Minute advance days must be between 1 and 30');
        }
      }
    }

    if (this.isMemberOnly) {
      const invalidTiers = this.memberTiers.filter(t => t.value == null || t.value <= 0);
      if (invalidTiers.length > 0) {
        errors.push('All member tiers must have a discount value greater than 0');
      }
      if (this.selectedDiscountType === 'MULTI_NIGHT') {
        if (v.applicableNight == null || v.applicableNight < 2) {
          errors.push('Applicable night must be >= 2 for multi-night discount');
        }
        if (v.stayDurationMin != null && v.applicableNight != null && v.stayDurationMin < v.applicableNight) {
          errors.push('Min stay duration must be >= applicable night');
        }
      }
    }

    if (v.stayDurationMin != null && v.stayDurationMax != null && v.stayDurationMin > v.stayDurationMax) {
      errors.push('Min stay duration must be <= max stay duration');
    }

    for (let i = 0; i < this.blackoutDates.length; i++) {
      const bd = this.blackoutDates[i];
      if (!bd.from || !bd.to) {
        errors.push(`Blackout date row ${i + 1}: both From and To are required`);
      } else if (moment(bd.from).isAfter(moment(bd.to))) {
        errors.push(`Blackout date row ${i + 1}: From must be before To`);
      }
    }

    return errors;
  }

  // Build payload for a single property
  buildPayload(propertyId: string): any {
    const v = this.form.value;
    const type = v.promotionType;
    const payload: any = {
      propertyId: propertyId,
      status: v.status
    };

    // Travel date range
    if (v.travelDateFrom && v.travelDateTo) {
      payload.travelDateRange = {
        from: moment(v.travelDateFrom).format('YYYY-MM-DD'),
        to: moment(v.travelDateTo).format('YYYY-MM-DD')
      };
    }

    // Stay duration
    if (v.stayDurationMin != null || v.stayDurationMax != null) {
      payload.stayDurationRange = {};
      if (v.stayDurationMin != null) payload.stayDurationRange.min = v.stayDurationMin;
      if (v.stayDurationMax != null) payload.stayDurationRange.max = v.stayDurationMax;
    }

    // Advance booking days
    if (v.advanceBookingMin != null && v.advanceBookingMax != null) {
      payload.advanceBookingDaysRange = {
        min: v.advanceBookingMin,
        max: v.advanceBookingMax
      };
    }

    // Blackout dates
    if (this.blackoutDates.length > 0) {
      payload.blackoutDates = this.blackoutDates
        .filter(bd => bd.from && bd.to)
        .map(bd => ({
          travelDateFrom: moment(bd.from).format('YYYY-MM-DD'),
          travelDateTo: moment(bd.to).format('YYYY-MM-DD')
        }));
    }

    // Discount
    if (type !== 'MEMBER_ONLY') {
      if (this.isDayOfWeekEligible && this.selectedDiscountMode === 'DAY_OF_WEEK') {
        const dow: any = {};
        this.dayOfWeekRows.forEach(d => {
          dow[d.day] = { unit: d.unit, value: d.value };
        });
        payload.dayOfWeekDiscount = dow;
      } else if (type === 'NEW_LISTING' && !this.overrideDiscount) {
        // Default 20% for new listing -- omit discountUnit/discountValue
      } else {
        if (v.discountUnit) payload.discountUnit = v.discountUnit;
        if (v.discountValue != null) payload.discountValue = v.discountValue;
      }
    }

    // Member Only specific
    if (type === 'MEMBER_ONLY') {
      payload.tiers = this.memberTiers.map(t => ({
        memberTier: t.memberTier,
        discount: { unit: t.unit, value: t.value }
      }));
      if (this.selectedDiscountType === 'MULTI_NIGHT') {
        payload.applicableNight = v.applicableNight;
        payload.recurring = v.recurring;
      }
    }

    return payload;
  }

  buildWikiCouponPayload(): any {
    const v = this.form.value;
    const payload: any = {
      couponCode: (v.couponCode || '').toUpperCase(),
      couponName: v.couponName || v.couponCode,
      status: v.status,
      discountValue: v.discountValue,
      discountUnit: v.discountUnit || 'PERCENT',
      isAutoApplied: v.isAutoApplied || false
    };
    if (v.bookingWindowFrom) {
      payload.bookingWindowFrom = moment(v.bookingWindowFrom).format('YYYY-MM-DD');
    }
    if (v.bookingWindowTo) {
      payload.bookingWindowTo = moment(v.bookingWindowTo).format('YYYY-MM-DD');
    }
    if (v.travelDateFrom) {
      payload.travelDateFrom = moment(v.travelDateFrom).format('YYYY-MM-DD');
    }
    if (v.travelDateTo) {
      payload.travelDateTo = moment(v.travelDateTo).format('YYYY-MM-DD');
    }
    if (!v.applyToAllProperties && this.resolvedProperties.length > 0) {
      payload.applicablePropertyIds = this.resolvedProperties.map((p: any) => p.shortTermRentalId);
    }
    return payload;
  }

  // Get the service method for the selected promotion type
  getCreateMethod(type: string): ((data: any) => any) | null {
    switch (type) {
      case 'EARLY_BOOKING': return (data: any) => this.vrboService.createEarlyBookingPromotion(data);
      case 'LAST_MINUTE': return (data: any) => this.vrboService.createLastMinutePromotion(data);
      case 'NEW_LISTING': return (data: any) => this.vrboService.createNewListingDiscountPromotion(data);
      case 'MEMBER_ONLY': return (data: any) => this.vrboService.createMemberOnlyPromotion(data);
      case 'MOBILE_ONLY': return (data: any) => this.vrboService.createMobileOnlyPromotion(data);
      case 'WIKI_COUPON': return (data: any) => this.vrboService.createWikiCoupon(data);
      default: return null;
    }
  }

  // Submit
  onSubmit(): void {
    const errors = this.validate();
    if (errors.length > 0) {
      errors.forEach(e => this.messageService.add({ severity: 'error', summary: 'Validation Error', detail: e }));
      return;
    }

    if (this.form.value.promotionType === 'WIKI_COUPON') {
      this.createWikiCoupon();
      return;
    }

    if (this.resolvedCount === 1) {
      this.createSingle();
    } else {
      this.openConfirmDialog();
    }
  }

  createWikiCoupon(): void {
    const payload = this.buildWikiCouponPayload();
    this.formLoading = true;

    if (this.isEditMode && this.editPromotionId) {
      payload.promotionId = this.editPromotionId;
      this.vrboService.updateWikiCoupon(payload).subscribe(
        (res: any) => {
          this.formLoading = false;
          this.messageService.add({ severity: 'success', summary: 'Success', detail: res?.message || 'Wiki coupon updated successfully' });
        },
        (err: any) => {
          this.formLoading = false;
          this.messageService.add({ severity: 'error', summary: 'Failed', detail: 'Failed to update wiki coupon: ' + (err?.error?.message || err?.message || err) });
        }
      );
    } else {
      this.vrboService.createWikiCoupon(payload).subscribe(
        (res: any) => {
          this.formLoading = false;
          this.messageService.add({ severity: 'success', summary: 'Success', detail: res?.message || 'Wiki coupon created successfully' });
        },
        (err: any) => {
          this.formLoading = false;
          this.messageService.add({ severity: 'error', summary: 'Failed', detail: 'Failed to create wiki coupon: ' + (err?.error?.message || err?.message || err) });
        }
      );
    }
  }

  createSingle(): void {
    const prop = this.resolvedProperties[0];
    const propertyId = prop.vrboId || String(prop.shortTermRentalId);
    const payload = this.buildPayload(propertyId);
    const createMethod = this.getCreateMethod(this.form.value.promotionType);

    if (!createMethod) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Unknown promotion type' });
      return;
    }

    this.formLoading = true;
    createMethod(payload).subscribe(
      (res: any) => {
        this.formLoading = false;
        this.messageService.add({ severity: 'success', summary: 'Success', detail: res?.message || 'Promotion created successfully' });
      },
      (err: any) => {
        this.formLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Failed', detail: 'Failed to create promotion: ' + (err?.error?.message || err?.message || err) });
      }
    );
  }

  // Bulk flow
  openConfirmDialog(): void {
    this.bulkProperties = this.resolvedProperties.map((p: any) => ({
      propertyId: p.vrboId || String(p.shortTermRentalId),
      propertyCode: p.propertyCode || p.displayLabel || String(p.shortTermRentalId),
      shortTermRentalId: String(p.shortTermRentalId || ''),
      selected: true,
      status: '',
      error: ''
    }));
    this.showConfirmDialog = true;
  }

  get selectedBulkCount(): number {
    return this.bulkProperties.filter(p => p.selected).length;
  }

  get activeBulkProperties(): BulkProperty[] {
    return this.bulkProperties.filter(p => p.status !== 'skipped');
  }

  toggleSelectAll(selectAll: boolean): void {
    this.bulkProperties.forEach(p => p.selected = selectAll);
  }

  async startBulkCreation(): Promise<void> {
    this.showConfirmDialog = false;

    const selected = this.bulkProperties.filter(p => p.selected);
    if (selected.length === 0) return;

    this.bulkTotal = selected.length;
    this.bulkProgress = 0;
    this.bulkSuccessCount = 0;
    this.bulkFailCount = 0;
    this.bulkComplete = false;
    this.bulkCurrentProperty = '';
    this.showProgressDialog = true;

    // Reset statuses
    this.bulkProperties.forEach(p => {
      p.status = p.selected ? 'pending' : 'skipped';
      p.error = '';
    });

    const createMethod = this.getCreateMethod(this.form.value.promotionType);
    if (!createMethod) return;

    for (const prop of this.bulkProperties) {
      if (!prop.selected) continue;

      prop.status = 'running';
      this.bulkCurrentProperty = prop.propertyCode;

      try {
        const payload = this.buildPayload(prop.propertyId);
        await createMethod(payload).toPromise();
        prop.status = 'success';
        this.bulkSuccessCount++;
      } catch (err: any) {
        prop.status = 'failed';
        prop.error = err?.error?.message || err?.message || 'Unknown error';
        this.bulkFailCount++;
      }

      this.bulkProgress = this.bulkSuccessCount + this.bulkFailCount;
    }

    this.bulkComplete = true;
    this.bulkCurrentProperty = '';
  }

  async retryFailed(): Promise<void> {
    const failed = this.bulkProperties.filter(p => p.status === 'failed');
    if (failed.length === 0) return;

    this.bulkTotal = failed.length;
    this.bulkProgress = 0;
    this.bulkSuccessCount = 0;
    this.bulkFailCount = 0;
    this.bulkComplete = false;

    const createMethod = this.getCreateMethod(this.form.value.promotionType);
    if (!createMethod) return;

    for (const prop of failed) {
      prop.status = 'running';
      prop.error = '';
      this.bulkCurrentProperty = prop.propertyCode;

      try {
        const payload = this.buildPayload(prop.propertyId);
        await createMethod(payload).toPromise();
        prop.status = 'success';
        this.bulkSuccessCount++;
      } catch (err: any) {
        prop.status = 'failed';
        prop.error = err?.error?.message || err?.message || 'Unknown error';
        this.bulkFailCount++;
      }

      this.bulkProgress = this.bulkSuccessCount + this.bulkFailCount;
    }

    this.bulkComplete = true;
    this.bulkCurrentProperty = '';
  }

  closeProgressDialog(): void {
    this.showProgressDialog = false;
  }

  // Reset
  onReset(): void {
    this.form.reset({
      selectionMode: 'BY_UNIT',
      selectedUnits: [],
      selectedLocation: null,
      selectedDevelopments: [],
      selectedLocations: [],
      promotionType: null,
      status: 'ACTIVE',
      discountUnit: 'PERCENT',
      discountValue: null,
      travelDateFrom: null,
      travelDateTo: null,
      advanceBookingMin: null,
      advanceBookingMax: null,
      stayDurationMin: 1,
      stayDurationMax: 180,
      applicableNight: null,
      recurring: false
    });
    this.isEditMode = false;
    this.editPromotionId = null;
    this.blackoutDates = [];
    this.resolvedProperties = [];
    this.resolvedCount = 0;
    this.showPreview = false;
    this.overrideDiscount = false;
    this.selectedDiscountType = 'FLAT';
    this.selectedDiscountMode = 'SINGLE';
    this.dayOfWeekRows = [
      { dayLabel: 'Monday', day: 'monday', unit: 'PERCENT', value: null },
      { dayLabel: 'Tuesday', day: 'tuesday', unit: 'PERCENT', value: null },
      { dayLabel: 'Wednesday', day: 'wednesday', unit: 'PERCENT', value: null },
      { dayLabel: 'Thursday', day: 'thursday', unit: 'PERCENT', value: null },
      { dayLabel: 'Friday', day: 'friday', unit: 'PERCENT', value: null },
      { dayLabel: 'Saturday', day: 'saturday', unit: 'PERCENT', value: null },
      { dayLabel: 'Sunday', day: 'sunday', unit: 'PERCENT', value: null }
    ];
    this.memberTiers = [
      { tierLabel: 'Blue', memberTier: 'BLUE', unit: 'PERCENT', value: 10 },
      { tierLabel: 'Silver', memberTier: 'SILVER', unit: 'PERCENT', value: 15 },
      { tierLabel: 'Gold', memberTier: 'GOLD', unit: 'PERCENT', value: 20 }
    ];
  }

  // Helper for promotion type label
  getPromotionTypeLabel(): string {
    const found = this.promotionTypes.find(t => t.value === this.form.value.promotionType);
    return found ? found.label : '';
  }
}
